import Notification from "../components/Notification";
import React, { useState, useEffect, useMemo } from "react";
import Tag from "../components/Tag";
import AuthenticatedLayout from "../layouts/AuthenticatedLayout";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import InputField from "../components/InputField";
import { createTag, deleteTag, getTags, updateTag } from "../services/tagService";
import Loading from "../components/Loading";
import { useAuth } from "../hooks/AuthProvider";

const TagPage = () => {
	const [tags, setTags] = useState([]);
	const [newTag, setNewTag] = useState("");
	const [newColor, setNewColor] = useState("#ffffff");
	const [editingTag, setEditingTag] = useState(null);
	const [editingTagName, setEditingTagName] = useState("");
	const [editingTagColor, setEditingTagColor] = useState("");
	const [editingTagHidden, setEditingTagHidden] = useState(false);
	const [errorEditingTag, setErrorEditingTag] = useState();
	const [error, setError] = useState();
	const [success, setSuccess] = useState();
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const auth = useAuth();

	useEffect(() => {
		setLoading(true);
		getTags(auth)
			.then((data) => setTags(Array.isArray(data) ? data : []))
			.catch(setError)
			.finally(() => setLoading(false));
	}, [auth]);

	const handleClickTag = (id) => {
		navigate(`/show?tag=${id}`);
	};

	// Aggiungi
	const handleAddTag = (e) => {
		e.preventDefault();
		setError(null);
		setSuccess(null);
		if (!newTag || !newColor) {
			setError("Nome e colore sono obbligatori");
			return;
		}
		createTag(auth, newTag, newColor)
			.then((res) => {
				const newTagObj = {
					id: res.id,
					tag: newTag,
					hex_color: newColor,
					hidden: false,
					canDelete: 1,
				};
				setTags((prev) => [...prev, newTagObj]);
				setNewTag("");
				setNewColor("#ffffff");
				setSuccess("Tag aggiunto con successo");
			})
			.catch(setError);
	};

	// Entra in modifica
	const handleEditTag = (tag) => {
		setEditingTag(tag);
		setEditingTagName(tag.tag);
		setEditingTagColor(tag.hex_color);
		setEditingTagHidden(!!tag.hidden);
	};

	// Salva modifica
	const handleSaveEdit = () => {
		setErrorEditingTag(null);
		setError(null);
		setSuccess(null);
		if (!editingTagName || !editingTagColor) {
			setErrorEditingTag("Nome e colore sono obbligatori");
			return;
		}

		updateTag(auth, editingTag.id, editingTagName, editingTagColor, editingTagHidden)
			.then((res) => {
				const updatedTags = tags.map((t) =>
					t.id === editingTag.id
						? {
								...t,
								tag: editingTagName,
								hex_color: editingTagColor,
								hidden: !!editingTagHidden,
						  }
						: t
				);
				setTags(updatedTags);
				setEditingTag(null);
				setEditingTagName("");
				setEditingTagColor("");
				setEditingTagHidden(false);
				setSuccess("Tag aggiornato con successo");
			})
			.catch(setError);
	};

	// Elimina
	const handleDeleteTag = (tagId) => {
		setError(null);
		setSuccess(null);
		deleteTag(auth, tagId)
			.then(() => {
				setTags(tags.filter((tag) => tag.id !== tagId));
			})
			.catch(setError);
	};

	// Toggle hidden
	const handleToggleHidden = (tag) => {
		// aggiorna solo hidden, lasciando gli altri valori invariati
		updateTag(auth, tag.id, undefined, "#c0c0c0", !tag.hidden)
			.then(() => {
				setTags((prev) =>
					prev.map((t) =>
						t.id === tag.id ? { ...t, hidden: !t.hidden, hex_color: "#c0c0c0" } : t
					)
				);
			})
			.catch(setError);
	};

	const visibleTags = useMemo(() => tags.filter((t) => !t.hidden), [tags]);
	const hiddenTags = useMemo(() => tags.filter((t) => !!t.hidden), [tags]);

	if (loading) return <Loading />;

	return (
		<AuthenticatedLayout title="gestione tag">
			{success && <Notification notification={{ type: 3, message: success }} />}
			{error && <Notification notification={{ type: 0, message: error }} />}

			{/* Form Nuovo Tag */}
			<form className="mb-5" onSubmit={handleAddTag}>
				<h2 className="subtitle">Aggiungi un Nuovo Tag</h2>
				<div className="columns">
					<div className="column">
						<InputField
							label="Nome tag"
							type="text"
							placeholder="Inserisci il nome del Tag"
							value={newTag}
							onChange={(e) => setNewTag(e.target.value)}
						/>
					</div>
					<div className="column">
						<div className="field">
							<label className="label">Colore</label>
							<div className="control">
								<input
									className="input"
									type="color"
									value={newColor}
									onChange={(e) => setNewColor(e.target.value)}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className="has-text-centered">
					<button className="button is-primary is-rounded mt-0 is-right">
						Aggiungi Tag
					</button>
				</div>
			</form>

			{/* Sezione Visibili */}
			<div className="mb-6">
				<h2 className="subtitle">Tag Visibili</h2>
				<div className="tags is-flex is-justify-content-center is-flex-wrap-wrap">
					{visibleTags.length === 0 && (
						<p className="has-text-grey">Nessun tag visibile.</p>
					)}
					{visibleTags.map((tag) => (
						<div key={tag.id} className="tag-container mx-1 my-3">
							<Tag
								id={tag.id}
								text={tag.tag}
								hex_color={tag.hex_color}
								onClick={handleClickTag}
							/>
							<div className="actions m-1 is-flex is-justify-content-center">
								<button
									className="button is-outlined is-info is-small mr-1"
									onClick={() => handleEditTag(tag)}
								>
									<FaEdit />
								</button>
								<button
									className="button is-outlined is-warning is-small mr-1"
									onClick={() => handleToggleHidden(tag)}
									title="Nascondi"
								>
									Nascondi
								</button>
								{tag.canDelete === 1 && (
									<button
										className="button is-outlined is-danger is-small"
										onClick={() => handleDeleteTag(tag.id)}
									>
										<FaTrashAlt />
									</button>
								)}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Sezione Nascosti */}
			<div>
				<h2 className="subtitle">Tag Nascosti</h2>
				<div className="tags is-flex is-justify-content-center is-flex-wrap-wrap">
					{hiddenTags.length === 0 && (
						<p className="has-text-grey">Nessun tag nascosto.</p>
					)}
					{hiddenTags.map((tag) => (
						<div key={tag.id} className="tag-container mx-1 my-3">
							<Tag
								id={tag.id}
								text={tag.tag}
								hex_color={tag.hex_color}
								onClick={handleClickTag}
							/>
							<div className="actions m-1 is-flex is-justify-content-center">
								<button
									className="button is-outlined is-info is-small mr-1"
									onClick={() => handleEditTag(tag)}
								>
									<FaEdit />
								</button>
								<button
									className="button is-outlined is-success is-small mr-1"
									onClick={() => handleToggleHidden(tag)}
									title="Mostra"
								>
									Mostra
								</button>
								{tag.canDelete === 1 && (
									<button
										className="button is-outlined is-danger is-small"
										onClick={() => handleDeleteTag(tag.id)}
									>
										<FaTrashAlt />
									</button>
								)}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Modale Modifica */}
			{editingTag && (
				<div className="modal is-active">
					<div className="modal-background" onClick={() => setEditingTag(null)}></div>
					<div className="modal-content">
						<div className="box">
							<h2 className="subtitle">Modifica Tag</h2>
							<div className="field">
								<label className="label">Nome del Tag</label>
								<div className="control">
									<input
										className="input"
										type="text"
										value={editingTagName}
										onChange={(e) => setEditingTagName(e.target.value)}
									/>
								</div>
							</div>
							<div className="field">
								<label className="label">Colore</label>
								<div className="control">
									<input
										className="input"
										type="color"
										value={editingTagColor}
										onChange={(e) => setEditingTagColor(e.target.value)}
									/>
								</div>
							</div>
							<div className="field">
								<label className="checkbox">
									<input
										type="checkbox"
										className="mr-2"
										checked={editingTagHidden}
										onChange={(e) => setEditingTagHidden(e.target.checked)}
									/>
									Nascondi
								</label>
							</div>

							{errorEditingTag && (
								<p className="help is-danger is-size-5 has-text-centered mx-4">
									{errorEditingTag}
								</p>
							)}

							<div className="control has-text-right">
								<button
									className="button is-secondary mr-2"
									onClick={() => setEditingTag(null)}
								>
									Annulla
								</button>
								<button className="button is-primary" onClick={handleSaveEdit}>
									Salva Modifiche
								</button>
							</div>
						</div>
					</div>
					<button
						className="modal-close is-large"
						onClick={() => setEditingTag(null)}
					></button>
				</div>
			)}
		</AuthenticatedLayout>
	);
};

export default TagPage;
